import { useParams } from "react-router-dom";
import { IProjectConfiguration } from "../entities-generated/models/project/project-configuration";
import { ObjectMap } from "../framework/entities/object-map";
import { forEach } from "../framework/utils/collection";
import { Shopability } from "./entities/shopability";
import { PageStart } from "./get-started/pages/sign-in";
import { PageStormDoorColors } from "./get-started/pages/storm-door-color";
import { HomePage } from "./home";
import { ProjectService } from "./services/project";
import { IShopAppRoute, IShopAppRouteBase } from "./shop-app-route";
import { ProjectStatus } from "../entities-generated/common/project-status";
import { PageShopStormDoors } from "./shop/pages/storm-door";
import { InstallationState } from "../entities-generated/common/installation-state";
import { toKebabCase } from "../framework/utils/string";
import { DoorType } from "../entities-generated/common/door-type";
import { PageShopStormHandles } from "./shop/pages/storm-handle";
import { PageCheckout } from "./purchase/pages/checkout";
import { PageMyOrder } from "./purchase/pages/my-order";

// use to build a regex that will match to a URL path without a bunch of escaping:
// e.g. /customers/:guid
// note that the URL starts with a slash.
// Placeholders are what make this useful. Use :guid to refer to a guid id (no dashes), and :int to refer to a whole number identifier
const buildRegex = (template: string) => {
    let normalizedTemplate = template.trim();
    normalizedTemplate = normalizedTemplate[0] === '/' ? normalizedTemplate.substring(1) : normalizedTemplate;

    const pathParts = normalizedTemplate.split('/');
    const actualParts = pathParts.map(p => {
        if(p === ':guid') {
            return '[0-9a-f]{32}';
        }
        if(p === ':int') {
            return '[0-9]+';
        }
        return p;
    });

    const rx = `^\\/${actualParts.join('\\/')}$`;
    return new RegExp(rx, 'i');
};
buildRegex('/'); // suppress linter showing non-use warning

class AppRouteConfig {
    public home: IShopAppRoute = {
        key: 'home',
        urlTemplate: '/',
        url: () => '/',
        icon: 'Home',
        exact: true,
        regex: null,
        topLevel: true,
        allowAnonymous: false,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: false,
            accountPanel: false
        },
        isContextCompatible: () => true,
        component: HomePage,
    };

    public stormDoorColorPreferences: IShopAppRoute = {
        key: 'storm-door-color-preferences',
        urlTemplate: '/start/color-preferences/storm-door',
        url: () => '/start/color-preferences/storm-door',
        icon: '',
        exact: true,
        regex: null,
        topLevel: true,
        allowAnonymous: false,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: false,
            accountPanel: true
        },
        isContextCompatible: (pc: IProjectConfiguration) => pc.stormDoorColorPreferences.length === 0,
        component: PageStormDoorColors,
    };

    public startWithCatalogCode: IShopAppRoute<(code: string) => string> = {
        key: 'start-with-catalog-code',
        urlTemplate: '/start/:code',
        url: (code: string) => `/start/${code}`,
        icon: '',
        exact: true,
        regex: /^\/start\/[a-z0-9]{4}$/i,
        topLevel: true,
        allowAnonymous: true,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: false,
            accountPanel: false
        },
        isContextCompatible: () => true,
        component: PageStart,
    };

    public start: IShopAppRoute = {
        key: 'start',
        urlTemplate: '/start',
        url: () => '/start',
        icon: '',
        exact: true,
        regex: null,
        topLevel: true,
        allowAnonymous: true,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: false,
            accountPanel: false
        },
        isContextCompatible: () => true,
        component: PageStart,
    };

    public selectStormDoor: IShopAppRoute<(location: string) => string> = {
        key: 'select-storm-door',
        urlTemplate: '/select/doors/storm-door/:location',
        url: (location: string) => `/select/doors/storm-door/${location}`,
        icon: '',
        exact: true,
        regex: /^\/select\/doors\/storm-door\/([a-z]+(?:-[a-z]+)*)$/i,
        topLevel: true,
        allowAnonymous: false,
        headerConfig: {
            doorSelectionTools: true,
            cartPanel: true,
            accountPanel: true
        },
        isContextCompatible: (pc: IProjectConfiguration) => {
            return ProjectService.getShoppingState(pc) === Shopability.Shoppable
                && pc.stormDoorColorPreferences.length > 0;
        },
        component: PageShopStormDoors,
    };

    public selectStormHandle: IShopAppRoute<(location: string) => string> = {
        key: 'select-storm-handle',
        urlTemplate: '/select/door-handles/storm-door/:location',
        url: (location: string) => `/select/door-handles/storm-door/${location}`,
        icon: '',
        exact: true,
        regex: /^\/select\/door-handles\/storm-door\/([a-z]+(?:-[a-z]+)*)$/i,
        topLevel: true,
        allowAnonymous: false,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: true,
            accountPanel: true
        },
        isContextCompatible: (pc: IProjectConfiguration) => {
            return ProjectService.getShoppingState(pc) === Shopability.Shoppable
                && pc.stormDoorColorPreferences.length > 0;
        },
        component: PageShopStormHandles,
    };

    public checkout: IShopAppRoute = {
        key: 'checkout',
        urlTemplate: '/checkout',
        url: () => '/checkout',
        icon: 'ShoppingCart',
        exact: true,
        regex: null,
        topLevel: true,
        allowAnonymous: false,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: true,
            accountPanel: true
        },
        isContextCompatible: (pc: IProjectConfiguration) => {
            return pc.status === ProjectStatus.ProjectPlanned;
        },
        component: PageCheckout,
    };

    public myOrder: IShopAppRoute<(id: string) => string> = {
        key: 'my-order',
        urlTemplate: '/my-order/:id',
        url: (id: string) => `/my-order/${id}`,
        icon: 'ShopServer',
        exact: true,
        regex: /^\/my-order\/([^/]{1,64})$/i,
        topLevel: true,
        allowAnonymous: true,
        headerConfig: {
            doorSelectionTools: false,
            cartPanel: false,
            accountPanel: false
        },
        isContextCompatible: () => true,
        component: PageMyOrder,
    };
}

export const AppRoutes = new AppRouteConfig();

export function getQueryStringValue(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
}

export function getAppRouteByKey(key: string): IShopAppRouteBase | undefined {
    const appRoutes = AppRoutes as unknown as ObjectMap<IShopAppRouteBase>;
    let selectedRoute: IShopAppRouteBase | undefined = undefined;

    forEach(appRoutes, route => {
        if(route.key === key) {
            selectedRoute = route;
            return false; // break
        }
    });

    return selectedRoute;
}

export const getAppRouteByPathname = (pathname: string): IShopAppRouteBase | undefined => {
    const appRoutes = AppRoutes as unknown as ObjectMap<IShopAppRouteBase>;
    let matchingRoute: IShopAppRouteBase | undefined = undefined;

    forEach(appRoutes, route => {
        if((!!route.regex && route.regex.test(pathname)) ||
        (!route.regex && route.urlTemplate === pathname.toLowerCase())) {
            matchingRoute = route;
            return false; // break;
        }
    });

    return matchingRoute;
}

export const getAppRoutesArray = (): IShopAppRouteBase[] => {
    const appRoutes = AppRoutes as unknown as ObjectMap<IShopAppRouteBase>;
    const routes: IShopAppRouteBase[] = [];

    forEach(appRoutes, route => {
        routes.push(route);
    });

    return routes;
};

export const useProjectInstallationSite = () => {
    const { location } = useParams<{ location: string }>();
    return location;
};

export const getNextShoppingUrl = (pc: IProjectConfiguration) => {
    if(ProjectService.getShoppingState(pc) !== Shopability.Shoppable) { return AppRoutes.home.url(); }
    if(pc.stormDoorColorPreferences.length === 0) { return AppRoutes.stormDoorColorPreferences.url(); }
    if(pc.status === ProjectStatus.ProjectPlanned) { return AppRoutes.checkout.url(); }

    const nextInstallSite = ProjectService.getNextIncompleteInstall(pc);
    // this should never happen, but just in case
    if(!nextInstallSite) { return AppRoutes.checkout.url(); }

    const nextDoor = nextInstallSite.doorInstallations[0];
    const location = toKebabCase(nextInstallSite.name);
    if(nextDoor.doorType === DoorType.StormDoor) {
        return nextDoor.installationState === InstallationState.NeedsDoorSelection
            ? AppRoutes.selectStormDoor.url(location)
            : AppRoutes.selectStormHandle.url(location);
    }

    // unconfigured door type
    return AppRoutes.start.url();
}